import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { Package, Receipt, RefreshCw, Copy, ArrowLeftRight, Clock, ChevronDown, ChevronRight, Filter } from "lucide-react";
import bit3 from "@/components/icons/bits/bit-3blade-render.png";
import bit4 from "@/components/icons/bits/bit-4blade-render.png";
import bit5 from "@/components/icons/bits/bit-5blade-render.png";
import bit6 from "@/components/icons/bits/bit-6blade-render.png";
import bitTricone from "@/components/icons/bits/bit-tricone-render.png";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Bit family render + label — mirrors StockCenterBrowse so an activity row reads
// with the same visual vocabulary as the cards/funnel. "Other" has no render.
const FAMILY_IMG = { "3 Blade": bit3, "4 Blade": bit4, "5 Blade": bit5, "6 Blade": bit6, "Tricone": bitTricone };
const FAMILY_LABEL = { "Tricone": "VRS / Tricone" };
const famLabel = (f) => FAMILY_LABEL[f] || f;
// Derive the funnel family from a bit doc (matches backend _sc_family).
function bitFamily(b) {
  if (!b) return "";
  const ptype = String(b.product_type || b.body_type || "").toUpperCase();
  const blades = String(b.blades || "");
  if (ptype.includes("TRICONE") || blades.toUpperCase().includes("CONE")) return "Tricone";
  const m = blades.match(/^\s*(\d+)\s*Blade\s*$/i);
  if (m) return `${m[1]} Blade`;
  return "Other";
}

// Compact diagonal fraction for sizes/threads (e.g. 4-7/8" → 4-⁷⁄₈"). Display
// only; same treatment as the funnel's FmtFrac.
function FmtFrac({ children }) {
  const s = String(children ?? "");
  if (!s) return null;
  const parts = s.split(/(\d+\/\d+)/);
  const dig = { fontSize: "0.64em", lineHeight: 1 };
  return (
    <>
      {parts.map((p, i) => {
        const m = p.match(/^(\d+)\/(\d+)$/);
        if (!m) return <React.Fragment key={i}>{p}</React.Fragment>;
        return (
          <span key={i} style={{ whiteSpace: "nowrap", display: "inline-flex", alignItems: "center" }}>
            <span style={{ ...dig, transform: "translateY(-0.30em)" }}>{m[1]}</span>
            <span style={{ fontSize: "0.95em", opacity: 0.5, margin: "0 -0.03em" }}>⁄</span>
            <span style={{ ...dig, transform: "translateY(0.16em)" }}>{m[2]}</span>
          </span>
        );
      })}
    </>
  );
}

// Small family render thumbnail (or a quiet box for "Other"/unknown).
function FamilyThumb({ family }) {
  const img = FAMILY_IMG[family];
  if (!img) return null;
  return <img src={img} alt="" className="w-5 h-5 object-contain shrink-0" draggable={false} aria-hidden="true" />;
}

// The bit-identity line shown on an activity row: family thumb + label + size,
// then the thread as a blue chip (the funnel's vocabulary). Looks up the bit by
// marking from the warehouse's loaded set; renders nothing extra if unknown
// (e.g. a since-deleted bit) so the row still reads cleanly.
function BitIdentity({ bit }) {
  if (!bit) return null;
  const family = bitFamily(bit);
  const label = famLabel(family);
  const size = bit.bit_size_in || (bit.bit_size_mm ? `${bit.bit_size_mm} mm` : "");
  const thread = (bit.thread_size || "").trim();
  if (!label && !size && !thread) return null;
  return (
    <span className="inline-flex items-center gap-1.5 flex-wrap">
      <FamilyThumb family={family} />
      {(label || size) && (
        <span className="text-[11.5px] text-[hsl(var(--muted-foreground))]">
          {label}{label && size ? " · " : ""}{size && <FmtFrac>{size}</FmtFrac>}
        </span>
      )}
      {thread && (
        <span className="text-[10.5px] text-[#185FA5] dark:text-[#85B7EB] bg-[#185FA5]/10 border border-[#185FA5]/30 rounded-full px-1.5 py-0 inline-flex items-center whitespace-nowrap">
          <FmtFrac>{thread}</FmtFrac>
        </span>
      )}
    </span>
  );
}

// ── Stock Center activity feed (the "what changed, by whom, when" surface) ──
// Day-grouped, newest first; bulk actions (a CSV import, an Add-another batch)
// collapse into ONE expandable summary row so a single meaningful edit isn't
// buried under hundreds of near-identical rows. Scoped warehouse-side by the
// endpoint. Read-only; rows click through to the bit's detail drawer.

// Per-type presentation: tinted disc + icon + verb. Colour + icon (never colour
// alone) so the action reads at a glance and for colour-blind users.
const TYPE_META = {
  receive:   { verb: "Received",       icon: Package,        color: "#185FA5" },
  sell:      { verb: "Sold",           icon: Receipt,        color: "#0F6E56" },
  status:    { verb: "Status changed", icon: RefreshCw,      color: "#BA7517" },
  duplicate: { verb: "Duplicated",     icon: Copy,           color: "#5F5E5A" },
  transfer:  { verb: "Transferred",    icon: ArrowLeftRight, color: "#534AB7" },
};
const typeMeta = (t) => TYPE_META[t] || { verb: t || "Updated", icon: Clock, color: "#5F5E5A" };

const FILTER_TYPES = [
  ["", "All"], ["receive", "Received"], ["sell", "Sold"],
  ["status", "Status"], ["duplicate", "Duplicated"], ["transfer", "Transferred"],
];

// Relative time for the row ("just now", "2h ago", "Yesterday", else a date).
function relTime(iso) {
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return "";
  const s = Math.floor((Date.now() - t) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  const d = Math.floor(s / 86400);
  if (d === 1) return "yesterday";
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short" });
}

// Absolute timestamp (hover title) — full audit value.
function absTime(iso) {
  const d = new Date(iso);
  return Number.isFinite(d.getTime()) ? d.toLocaleString() : "";
}

// Day bucket label for the section header.
function dayLabel(iso) {
  const d = new Date(iso); d.setHours(0, 0, 0, 0);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const diff = Math.round((today - d) / 86400000);
  if (diff <= 0) return "Today";
  if (diff === 1) return "Yesterday";
  if (diff < 7) return new Date(iso).toLocaleDateString(undefined, { weekday: "long" });
  return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}

// The salient detail per type: who bought it, the status delta, where it moved.
// The note is only used as a fallback when it ADDS something — a bare "Received"
// note just repeats the verb, so it's suppressed.
function detailOf(t) {
  if (t.type === "sell" && t.customer) return `to ${t.customer}`;
  if (t.type === "status" && (t.old_status || t.new_status)) return `${t.old_status || "—"} → ${t.new_status || "—"}`;
  if (t.type === "transfer" && (t.from_warehouse || t.to_warehouse)) return `${t.from_warehouse || "—"} → ${t.to_warehouse || "—"}`;
  const note = (t.note || "").trim();
  const verb = (typeMeta(t.type).verb || "").trim();
  if (!note || note.toLowerCase() === verb.toLowerCase()) return "";
  return note;
}

// ── Bulk collapse ──
// Adjacent same-(user,type) events within a short window are almost always one
// action (a CSV import, an Add-another batch). Collapse them into one summary
// group with a count; the user can expand to see the individual markings.
const BULK_WINDOW_MS = 90 * 1000; // events within 90s of each other in a run
const BULK_MIN = 4;               // only collapse runs of 4+ (smaller runs read fine individually)

function groupBulk(txns) {
  const out = [];
  let run = [];
  const flush = () => {
    if (!run.length) return;
    if (run.length >= BULK_MIN) {
      out.push({ bulk: true, items: run, type: run[0].type, user: run[0].user,
                 user_role: run[0].user_role, warehouse: run[0].warehouse, timestamp: run[0].timestamp });
    } else {
      run.forEach((r) => out.push({ bulk: false, ...r }));
    }
    run = [];
  };
  for (const t of txns) {
    if (run.length) {
      const prev = run[run.length - 1];
      const sameRun = prev.user === t.user && prev.type === t.type &&
        Math.abs(new Date(prev.timestamp) - new Date(t.timestamp)) <= BULK_WINDOW_MS;
      if (!sameRun) flush();
    }
    run.push(t);
  }
  flush();
  return out;
}

// Marking → white outlined "Part#" pill; design → recessed grey code chip.
// Same treatment as the stock Table so a bit reads identically everywhere.
function MarkingPill({ children }) {
  return <span className="font-mono-data text-[11px] px-1.5 py-0.5 rounded-md bg-[hsl(var(--card))] border border-[hsl(var(--border))] rl-pill font-semibold text-[hsl(var(--foreground))]">{children}</span>;
}
function DesignPill({ children }) {
  return <span className="font-mono-data text-[10px] px-1.5 py-0.5 rounded-md rl-carved bg-[hsl(var(--muted))]/50 text-[hsl(var(--muted-foreground))]">{children}</span>;
}

function ActorLine({ t }) {
  const role = t.user_role ? ` (${t.user_role})` : "";
  return (
    <span className="text-[12px] text-[hsl(var(--muted-foreground))] inline-flex items-center gap-1.5 truncate">
      <span className="truncate">{t.user || "Unknown"}{role}</span>
      {t.design_no && <DesignPill>{t.design_no}</DesignPill>}
    </span>
  );
}

function TypeDisc({ type }) {
  const m = typeMeta(type);
  const Icon = m.icon;
  return (
    <span className="flex items-center justify-center w-8 h-8 rounded-full shrink-0"
          style={{ background: `${m.color}1a`, color: m.color }} aria-hidden="true">
      <Icon className="w-4 h-4" />
    </span>
  );
}

// A single (non-bulk) event row. `bit` is the looked-up bit doc (for the
// family/size/thread identity line) — may be undefined for a since-deleted bit.
function EventRow({ t, bit, showWarehouse, onOpenBit }) {
  const m = typeMeta(t.type);
  const detail = detailOf(t);
  return (
    <button type="button" onClick={() => onOpenBit?.(t)}
      className="w-full text-left flex items-center gap-3 px-4 py-2.5 hover:bg-[hsl(var(--muted))]/30 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#0F6E56]"
      data-testid="sc-activity-row">
      <TypeDisc type={t.type} />
      <div className="min-w-0 flex-1">
        <div className="text-[13px] text-[hsl(var(--foreground))] inline-flex items-center gap-1.5 max-w-full truncate">
          <span className="font-semibold shrink-0" style={{ color: m.color }}>{m.verb}</span>
          {t.marking_no && <MarkingPill>{t.marking_no}</MarkingPill>}
          {detail && <span className="text-[hsl(var(--muted-foreground))] truncate">{detail}</span>}
        </div>
        {/* Bit identity — family thumb + type · size + thread chip (funnel vocab). */}
        <div className="mt-0.5"><BitIdentity bit={bit} /></div>
        <ActorLine t={t} />
      </div>
      <div className="text-right shrink-0">
        <div className="text-[12px] text-[hsl(var(--muted-foreground))] whitespace-nowrap" title={absTime(t.timestamp)}>{relTime(t.timestamp)}</div>
        {showWarehouse && t.warehouse && (
          <span className="font-mono-data text-[10px] uppercase tracking-wide text-[hsl(var(--muted-foreground))]/80">{t.warehouse}</span>
        )}
      </div>
    </button>
  );
}

// A collapsed bulk group: one summary row that expands to its members.
function BulkRow({ g, showWarehouse, onOpenBit }) {
  const [open, setOpen] = useState(false);
  const m = typeMeta(g.type);
  const n = g.items.length;
  const Chevron = open ? ChevronDown : ChevronRight;
  return (
    <div data-testid="sc-activity-bulk">
      <button type="button" onClick={() => setOpen((o) => !o)}
        className="w-full text-left flex items-center gap-3 px-4 py-2.5 hover:bg-[hsl(var(--muted))]/30 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#0F6E56]"
        aria-expanded={open}>
        <TypeDisc type={g.type} />
        <div className="min-w-0 flex-1">
          <div className="text-[13px] text-[hsl(var(--foreground))] truncate">
            <span className="font-semibold" style={{ color: m.color }}>{m.verb}</span>
            <span className="font-mono-data"> {n}</span>
            <span className="text-[hsl(var(--muted-foreground))]"> bit{n === 1 ? "" : "s"}</span>
          </div>
          <span className="text-[12px] text-[hsl(var(--muted-foreground))] truncate">
            {g.user || "Unknown"}{g.user_role ? ` (${g.user_role})` : ""}
          </span>
        </div>
        <div className="text-right shrink-0 flex items-center gap-2">
          <div>
            <div className="text-[12px] text-[hsl(var(--muted-foreground))] whitespace-nowrap" title={absTime(g.timestamp)}>{relTime(g.timestamp)}</div>
            {showWarehouse && g.warehouse && (
              <span className="font-mono-data text-[10px] uppercase tracking-wide text-[hsl(var(--muted-foreground))]/80">{g.warehouse}</span>
            )}
          </div>
          <Chevron className="w-4 h-4 text-[hsl(var(--muted-foreground))]/60 shrink-0" aria-hidden="true" />
        </div>
      </button>
      {open && (
        <div className="bg-[hsl(var(--muted))]/20 border-y border-[hsl(var(--border))]/60">
          {g.items.map((t) => (
            <button key={t.id} type="button" onClick={() => onOpenBit?.(t)}
              className="w-full text-left flex items-center gap-3 pl-14 pr-4 py-1.5 hover:bg-[hsl(var(--muted))]/40 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#0F6E56]">
              {t.marking_no
                ? <MarkingPill>{t.marking_no}</MarkingPill>
                : <span className="font-mono-data text-[12px] text-[hsl(var(--muted-foreground))]/60">no serial</span>}
              {t.design_no && <DesignPill>{t.design_no}</DesignPill>}
              <span className="ml-auto text-[11px] text-[hsl(var(--muted-foreground))]/70 whitespace-nowrap" title={absTime(t.timestamp)}>{relTime(t.timestamp)}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function StockCenterActivity({ activeWh, onOpenBit }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [typeFilter, setTypeFilter] = useState("");

  // marking → bit doc, for the per-row family/size/thread identity line. The
  // activity txns only carry marking + design, so we join against the bits the
  // page already serves. Keyed "WAREHOUSE|MARKING" so markings that repeat
  // across warehouses don't collide on the ALL view.
  const [bitMap, setBitMap] = useState({});
  const bitKey = (wh, mk) => `${wh || ""}|${String(mk || "")}`;

  const fetchActivity = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const whq = activeWh && activeWh !== "ALL" ? `?warehouse=${activeWh}` : "";
      const [actRes, bitsRes] = await Promise.all([
        axios.get(`${API}/stock-center/activity?limit=200`),
        axios.get(`${API}/stock-center/bits${whq}`),
      ]);
      let data = actRes.data || [];
      // The endpoint scopes by the user's ALLOWED warehouses; narrow further to
      // the active tab so the feed matches what the rest of the page shows.
      if (activeWh && activeWh !== "ALL") data = data.filter((t) => t.warehouse === activeWh);
      // Build the lookup map (best-effort — a since-deleted bit just won't match).
      const map = {};
      (bitsRes.data || []).forEach((b) => { if (b.marking_no) map[bitKey(b.warehouse, b.marking_no)] = b; });
      setBitMap(map);
      setRows(data);
    } catch (e) {
      setError(e.response?.data?.detail || "Could not load activity");
    } finally { setLoading(false); }
  }, [activeWh]);

  useEffect(() => { fetchActivity(); }, [fetchActivity]);

  const showWarehouse = activeWh === "ALL";
  const bitFor = useCallback((t) => bitMap[bitKey(t.warehouse, t.marking_no)], [bitMap]);

  // Filter → bulk-collapse → day-group. (Filter first so counts reflect the
  // filtered set; collapse only same-type runs so a type filter never splits a
  // group oddly.)
  const sections = useMemo(() => {
    let list = rows;
    if (typeFilter) list = list.filter((t) => t.type === typeFilter);
    const grouped = groupBulk(list);
    const byDay = [];
    let cur = null;
    for (const g of grouped) {
      const label = dayLabel(g.timestamp);
      if (!cur || cur.label !== label) { cur = { label, items: [] }; byDay.push(cur); }
      cur.items.push(g);
    }
    return byDay;
  }, [rows, typeFilter]);

  return (
    <div data-testid="sc-activity" className="pb-1">
      {/* Type filter — secondary control; the day grouping + actor line do the
          heavy lifting, this just narrows to one action class when wanted.
          py-1.5 -my-1.5: the active chip lifts with a shadow (rl-thumb), and an
          overflow-x container would CLIP that shadow vertically — the padding
          gives it room, the negative margin keeps the visual position. */}
      <div className="flex items-center gap-1.5 px-1 py-1.5 -my-1.5 mb-2.5 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        <Filter className="w-3.5 h-3.5 text-[hsl(var(--muted-foreground))]/60 shrink-0" aria-hidden="true" />
        {FILTER_TYPES.map(([val, label]) => {
          const active = typeFilter === val;
          return (
            <button key={val || "all"} onClick={() => setTypeFilter(val)} data-testid={`sc-activity-filter-${val || "all"}`}
              className={`h-7 px-3 rounded-full text-[11px] font-semibold whitespace-nowrap shrink-0 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0F6E56] ${
                active ? "rl-thumb bg-[hsl(var(--card))] text-[hsl(var(--foreground))]" : "text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] hover:bg-[hsl(var(--foreground))]/[0.05]"
              }`}>{label}</button>
          );
        })}
      </div>

      {loading ? (
        <div className="rl-glass-edge rounded-xl bg-[hsl(var(--card))] overflow-hidden divide-y divide-[hsl(var(--border))]/60">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="flex items-center gap-3 px-4 py-3 animate-pulse" aria-hidden="true">
              <span className="w-8 h-8 rounded-full bg-[hsl(var(--muted-foreground))]/15 shrink-0" />
              <div className="flex-1 space-y-1.5">
                <div className="h-3 rounded bg-[hsl(var(--muted-foreground))]/15" style={{ width: `${50 + (i % 3) * 12}%` }} />
                <div className="h-2.5 rounded bg-[hsl(var(--muted-foreground))]/12" style={{ width: `${30 + (i % 2) * 14}%` }} />
              </div>
            </div>
          ))}
        </div>
      ) : error ? (
        <div className="flex flex-col items-center gap-3 py-14 text-center">
          <Clock className="w-9 h-9 text-[hsl(var(--muted-foreground))]/40" />
          <p className="text-[14px] text-[hsl(var(--foreground))]">{error}</p>
          <button onClick={fetchActivity} className="rl-lit-btn inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.05em] px-4 py-2 rounded-full text-white" style={{ backgroundColor: "#0F6E56" }}><RefreshCw className="w-4 h-4" /> Retry</button>
        </div>
      ) : sections.length === 0 ? (
        <div className="flex flex-col items-center gap-2 py-16 text-[hsl(var(--muted-foreground))]">
          <Clock className="w-9 h-9 opacity-40" />
          <span className="text-[14px] font-medium text-[hsl(var(--foreground))]">No activity {typeFilter ? "of this type " : ""}yet</span>
          <span className="text-[12px]">Receiving, selling, and edits will show up here.</span>
        </div>
      ) : (
        <div className="space-y-4">
          {sections.map((sec) => (
            <div key={sec.label}>
              <div className="text-[10px] font-bold uppercase tracking-[0.12em] text-[hsl(var(--muted-foreground))]/70 mb-1.5 px-1">{sec.label}</div>
              <div className="rl-glass-edge rounded-xl bg-[hsl(var(--card))] overflow-hidden divide-y divide-[hsl(var(--border))]/60">
                {sec.items.map((g, i) => (
                  g.bulk
                    ? <BulkRow key={`b-${i}-${g.timestamp}`} g={g} showWarehouse={showWarehouse} onOpenBit={onOpenBit} />
                    : <EventRow key={g.id} t={g} bit={bitFor(g)} showWarehouse={showWarehouse} onOpenBit={onOpenBit} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
